<script setup lang="ts">
import { computed } from "vue";
import { useNow } from "@vueuse/core";
import { UiAvatar, UiIcon, UiSquareButton, UiText, UiTooltip } from "@shopexperts/shared";
import { formatPeerLocalTime } from "../formatPeerLocalTime";

const props = defineProps<{
  side: "client" | "expert";
  name: string;
  peer?: { name: string; avatar_url: string | null; subtitle?: string | null; timezone?: string | null; profile_url?: string | null };
}>();
const emit = defineEmits<{ back: []; createProject: [] }>();
const subtitle = computed(() => props.peer?.subtitle?.trim() || null);
const now = useNow({ interval: 30_000 });
const localTime = computed(() => formatPeerLocalTime(props.peer?.timezone, now.value));
const profileUrl = computed(() => {
  const url = props.peer?.profile_url;
  if (!url) return undefined;
  try { return ["http:", "https:"].includes(new URL(url).protocol) ? url : undefined; } catch { return undefined; }
});
</script>

<template>
  <header class="shrink-0 border-b border-se-gray-200 bg-white px-3 py-3 sm:px-5">
    <div class="flex items-center gap-2 sm:gap-4">
      <UiTooltip content="Back to messages">
        <UiSquareButton icon="arrow-narrow-left" aria-label="Back to messages" class="shrink-0" @click="emit('back')" />
      </UiTooltip>
      <div class="flex min-w-0 flex-1 items-center gap-3">
        <UiAvatar v-if="peer" :name="peer.name" :url="peer.avatar_url ?? undefined" size="xs" :responsive="false" loading="eager" />
        <div v-else class="h-12 w-12 shrink-0 animate-pulse rounded-full bg-se-gray-100" aria-hidden="true" />
        <div class="conversation-identity flex min-w-0 flex-1 flex-col">
          <UiText as="h2" variant="h5" :text="name" class="truncate" :title="name" />
          <UiText v-if="subtitle" as="p" variant="small" :text="subtitle" class="truncate text-se-gray-600" :title="subtitle" />
          <UiTooltip v-if="localTime" :content="`${peer?.name ?? name}’s local time (${peer?.timezone})`">
            <div class="flex min-w-0 max-w-full cursor-default select-none items-center gap-1.5 self-start text-se-gray-500" tabindex="0">
              <UiIcon name="clock" class="h-4 w-4 shrink-0" />
              <UiText as="p" variant="small" :text="localTime" class="truncate" />
            </div>
          </UiTooltip>
        </div>
      </div>
      <div class="flex shrink-0 items-center gap-1 sm:gap-2" aria-label="Conversation actions">
        <UiTooltip v-if="side === 'client' && profileUrl" :content="`View ${peer?.name}’s profile`">
          <UiSquareButton icon="link-external" :href="profileUrl" target="_blank" aria-label="View expert profile" />
        </UiTooltip>
        <UiTooltip v-if="peer" :content="`Create a project with ${peer.name}. It will be shared in this conversation.`">
          <UiSquareButton icon="plus" variant="secondary" aria-label="Create a project" @click="emit('createProject')" />
        </UiTooltip>
        <div v-else class="h-11 w-11 animate-pulse rounded-sm bg-se-gray-100" aria-hidden="true" />
      </div>
    </div>
  </header>
</template>

<style scoped>
/* Compact identity stack: 22px name + two 18px details beside a 48px avatar. */
.conversation-identity :deep(h2) {
  margin: 0;
  line-height: 22px;
}
.conversation-identity :deep(p) {
  margin: 0;
  line-height: 18px;
}
</style>
